import api from "@/api";
import { Admin, NewAdminDetails } from "@/interfaces/admin.interface";
import i18n from "@/plugins/i18n";
import { defineStore } from "pinia";
import { useSchoolStore } from "./schoolStore";
import { useSchoolGroupStore } from "./schoolGroupsStore";

export const useAdminStore = defineStore({
  id: "adminStore",
  state: () => ({
    loader: {
      creatingAdmin: false,
      updatingAdmin: false,
      fetchingAllAdmins: false,
      fetchingSingleAdmin: false,
      deleteAdmin: false,
    },
    loadingMessage: "",
    admins: [] as Admin[],
    currentAdmin: null as Admin | null,
  }),
  actions: {
    async fetchAllSchoolAdmins(schoolId: string) {
      try {
        this.loader.fetchingAllAdmins = true;
        this.loadingMessage = i18n.global.t(
          "loaders.admin_store.creatingAdmin"
        );

        const response = await api.get(
          `/v2/organisation/school-admins/school/${schoolId}`
        );
        if (response.data.success) {
          this.admins = response.data.data;
        } else {
          console.error("Failed to fetch admins:", response.data.message);
        }
      } catch (error) {
        console.error("Error fetching admins:", error);
      } finally {
        this.loader.fetchingAllAdmins = false;
        this.loadingMessage = "";
      }
    },

    async fetchAdminById(adminId: string) {
      try {
        this.loader.fetchingSingleAdmin = true;
        this.loadingMessage = i18n.global.t(
          "loaders.admin_store.fetchingSingleAdmin"
        );

        const response = await api.get(
          `/v2/organisation/school-admins/${adminId}`
        );
        if (response.data.success) {
          this.currentAdmin = response.data.data;
        } else {
          console.error("Failed to fetch admin:", response.data.message);
        }
      } catch (error) {
        console.error("Error fetching admin:", error);
      } finally {
        this.loader.fetchingSingleAdmin = false;
        this.loadingMessage = "";
      }
    },

    async updateAdminDetails(adminId: string, adminDetails: Partial<Admin>) {
      try {
        this.loader.updatingAdmin = true;
        this.loadingMessage = i18n.global.t(
          "loaders.admin_store.updatingAdmin"
        );

        const response = await api.put(
          `/v2/organisation/school-admins/${adminId}`,
          adminDetails
        );
        if (response.data.success) {
          this.currentAdmin = response.data.data;
          // Optionally update the list of admins if it is already fetched
          const index = this.admins.findIndex((admin) => admin._id === adminId);
          if (index !== -1) {
            this.admins[index] = response.data.data;
          }
        } else {
          console.error("Failed to update admin:", response.data.message);
        }
      } catch (error) {
        console.error("Error updating admin:", error);
      } finally {
        this.loader.updatingAdmin = false;
        this.loadingMessage = "";
      }
    },

    async createNewAdmin(adminDetails: NewAdminDetails) {
      try {
        this.loader.creatingAdmin = true;
        this.loadingMessage = i18n.global.t(
          "loaders.admin_store.creatingAdmin"
        );

        const response = await api.post(
          "/v2/access/admin/initialise-new",
          adminDetails
        );
        if (response.data.success) {
          this.admins.push(response.data.data); // Add the newly created admin to the list
          return response.data.data;
        } else {
          console.error("Failed to create admin:", response.data.message);
          return false;
        }
      } catch (error) {
        console.error("Error creating admin:", error);
        return false;
      } finally {
        this.loader.creatingAdmin = false;
        this.loadingMessage = "";
      }
    },
    // Action to delete a user
    async deleteExistingChampion(uid: string, entityId: string, championType: 'school') {
      try {
        this.loader.deleteAdmin = true;
        this.loadingMessage = i18n.global.t(
          "loaders.admin_store.deleteAdmin"
        );
        const response = await api.delete(`/v2/access/admin/delete/${uid}`); // Make the DELETE request to your backend

        if (response.data.success) {
          if (championType === "school") {
            const schoolStore = useSchoolStore();
            await schoolStore.fetchSchoolStructure(entityId);
          } else {
            const schoolGroupStore = useSchoolGroupStore();
            await schoolGroupStore.fetchSchooGroupById(entityId);
          }
          return true;
        } else {
          console.error("Failed to create admin:", response.data.message);
          return false;
        }
      } catch (error) {
        console.error("Error deleting user:", error);
        return false;
      } finally {
        this.loader.deleteAdmin = false;
        this.loadingMessage = "";
      }
    },
    // Action to delete a user
    async deleteClassGroupTeacher(uid: string, schoolId: string) {
      try {
        this.loader.deleteAdmin = true;
        this.loadingMessage = i18n.global.t(
          "loaders.admin_store.deleteAdmin"
        );
        const response = await api.delete(`/v2/access/admin/delete/${uid}`); // Make the DELETE request to your backend

        if (response.data.success) {
          const schoolStore = useSchoolStore();
          await schoolStore.fetchSchoolStructure(schoolId);
          return true;
        } else {
          console.error("Failed to create admin:", response.data.message);
          return false;
        }
      } catch (error) {
        console.error("Error deleting user:", error);
        return false;
      } finally {
        this.loader.deleteAdmin = false;
        this.loadingMessage = "";
      }
    },
  },
  persist: true,
});
